import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="oms--oms--filter-overlay"
export default class extends Controller {
  static targets = ["panel", "backdrop"]

  connect() {
    console.log("OMS Filter Overlay controller connected")
    console.log("Controller methods:", Object.getOwnPropertyNames(Object.getPrototypeOf(this)))
    this.closeOnEscape = this.closeOnEscape.bind(this)
    this.formEventListeners = new Map()
    this.formSubmitTimeout = null
  }

  resetFilters() {
    console.log("resetFilters method called!")

    // Prevent any form submission during reset
    event.preventDefault()
    event.stopPropagation()

    // Clear all form inputs in the filter panel
    const form = this.panelTarget.querySelector('#quotes-filter-form')
    console.log("form", form)
    if (form) {
      console.log("Found form, resetting to defaults...")

      // Clear all form inputs to their default values
      try {
        console.log("About to reset form inputs...")
        form.querySelectorAll('input, select, textarea').forEach(input => {
          console.log("Resetting input:", input.name, input.type)
          if (input.type === 'checkbox' || input.type === 'radio') {
            // For checkboxes, uncheck all except the "All" status checkbox
            if (input.name === 'filter_status[]' && input.value === '') {
              // This is the "All" checkbox - keep it checked
              input.checked = true
              input.disabled = false
            } else {
              input.checked = false
            }
          } else if (input.tagName === 'SELECT') {
            // For select elements, reset to first option (usually the default)
            if (input.options.length > 0) {
              input.selectedIndex = 0
            }
          } else {
            // For text inputs, clear the value unless it is the status parameter
            if (input.name !== 'status') {
              input.value = ''
            }
          }
        })
        console.log("Form inputs reset successfully")
      } catch (error) {
        console.error("Error resetting form inputs:", error)
      }

      // Clear Uikit SelectSingle components (like client filter)
      const selectSingleComponents = form.querySelectorAll('[data-controller="uikit--select-single"]')
      selectSingleComponents.forEach(component => {
        try {
          const controller = this.application.getControllerForElementAndIdentifier(
            component, 'uikit--select-single'
          )
          if (controller && typeof controller.clear === 'function') {
            controller.clear()
          }
        } catch (error) {
          console.warn('Failed to clear select-single via controller:', error)
        }
      })

      // Clear multi-select visual displays (if any exist)
      const multiSelectComponents = form.querySelectorAll('[data-controller="uikit--select-multi"]')
      multiSelectComponents.forEach(component => {
        try {
          const controller = this.application.getControllerForElementAndIdentifier(
            component, 'uikit--select-multi'
          )
          if (controller && typeof controller.clear === 'function') {
            controller.clear()
          } else {
            // Fallback: manually clear the visual display
            this.clearMultiSelectDisplay(component)
          }
        } catch (error) {
          console.warn('Failed to clear multi-select via controller:', error)
          // Fallback: manually clear the visual display
          this.clearMultiSelectDisplay(component)
        }
      })

      // Clear client search controller if present
      const clientSearchContainer = form.querySelector('[data-controller*="client-search"]')
      if (clientSearchContainer) {
        try {
          const clientSearchController = this.application.getControllerForElementAndIdentifier(
            clientSearchContainer, 'oms--oms--client-search'
          )
          if (clientSearchController && typeof clientSearchController.clearSelection === 'function') {
            clientSearchController.clearSelection()
          }
        } catch (error) {
          console.warn('Failed to clear client search via controller:', error)
          // Fallback: manually clear client search fields
          const clientSearchInput = form.querySelector('input[name="client_search"]')
          if (clientSearchInput) {
            clientSearchInput.value = ''
            this.updateClientSearchIcon('')
          }
          const clientHiddenField = form.querySelector('input[name="settlement_account_id"]')
          if (clientHiddenField) {
            clientHiddenField.value = ''
          }

          const clientResults = form.querySelector('[data-oms--oms--client-search-target="results"]')
          if (clientResults) {
            clientResults.classList.add('hidden')
          }
        }
      }

      // Update search icons for quotes search
      const quotesSearchInput = form.querySelector('#quotes-search-input')
      if (quotesSearchInput) {
        this.updateQuotesSearchIcon('')
      }

      // Ensure panel stays open by preserving its visual state
      const panel = this.panelTarget
      const backdrop = this.backdropTarget
      const isPanelOpen = !panel.classList.contains('hidden') && !panel.classList.contains('translate-x-full')

      // Submit the form using the filter sidebar controller to trigger Turbo Stream
      console.log("Submitting form using filter sidebar controller...")

      // Add a small delay to ensure all form elements are properly reset
      setTimeout(() => {
        // Try to find the controller on the form itself first
        let filterSidebarController = this.application.getControllerForElementAndIdentifier(
          form,
          'oms--oms--filter-sidebar'
        )

        // If not found on form, try to find it on a parent element
        if (!filterSidebarController) {
          const formElement = form.closest('[data-controller*="oms--oms--filter-sidebar"]')
          console.log("Form element with controller:", formElement)

          filterSidebarController = this.application.getControllerForElementAndIdentifier(
            formElement,
            'oms--oms--filter-sidebar'
          )
        }

        console.log("Filter sidebar controller found:", !!filterSidebarController)
        console.log("Controller methods:", filterSidebarController ? Object.getOwnPropertyNames(Object.getPrototypeOf(filterSidebarController)) : 'No controller')

        if (filterSidebarController && typeof filterSidebarController.submitForm === 'function') {
          console.log("Calling filter sidebar submitForm")
          filterSidebarController.submitForm()
        } else if (filterSidebarController && typeof filterSidebarController.handleFilterChange === 'function') {
          console.log("Calling filter sidebar handleFilterChange")
          filterSidebarController.handleFilterChange()
        } else {
          console.error("Filter sidebar controller not found or missing submitForm method")
          console.log("Available methods:", filterSidebarController ? Object.getOwnPropertyNames(Object.getPrototypeOf(filterSidebarController)) : 'No controller')
          // Fallback: submit the form directly with Turbo
          console.log("Using fallback form submission with Turbo")

          // Use fetch with Turbo Stream headers to prevent page reload
          const formData = new FormData(form)
          const url = form.action + '?' + new URLSearchParams(formData).toString()

          fetch(url, {
            method: 'GET',
            headers: {
              'Accept': 'text/vnd.turbo-stream.html',
              'X-Requested-With': 'XMLHttpRequest',
              'Turbo-Frame': 'quotes_data'
            }
          })
          .then(response => {
            console.log('Response status:', response.status)
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            return response.text()
          })
          .then(html => {
            console.log('Received HTML:', html.substring(0, 200) + '...')
            // Parse and execute the Turbo Stream
            if (typeof Turbo !== 'undefined' && Turbo.renderStreamMessage) {
              Turbo.renderStreamMessage(html)
            } else {
              console.error('Turbo is not available')
              // Fallback: reload the page
              window.location.reload()
            }
          })
          .catch(error => {
            console.error('Error submitting form:', error)
            // Final fallback: try regular form submission
            console.log('Attempting final fallback form submission...')
            form.submit()
          })
        }
      }, 100)

      // Ensure status filters visibility is properly managed after reset
      setTimeout(() => {
        this.ensureStatusFiltersVisibility()
      }, 200)

      // After form submission, ensure panel stays open if it was open before
      setTimeout(() => {
        if (isPanelOpen) {
          // Ensure panel is visible and in the correct position
          panel.classList.remove('hidden')
          backdrop.classList.remove('hidden')
          panel.classList.remove('translate-x-full')
          panel.classList.add('translate-x-0')

          // Ensure body scroll is prevented
          document.body.classList.add('overflow-hidden')

          // Re-add event listeners
          this.addEventListeners()
        }
      }, 250)
    }
  }

  disconnect() {
    this.removeEventListeners()
  }

  openFilter() {
    // Show the overlay elements
    this.panelTarget.classList.remove("hidden")
    this.backdropTarget.classList.remove("hidden")

    // Trigger the slide-in animation
    requestAnimationFrame(() => {
      this.panelTarget.classList.remove("translate-x-full")
      this.panelTarget.classList.add("translate-x-0")
    })

    // Add escape key listener
    this.addEventListeners()

    // Prevent body scroll
    document.body.classList.add("overflow-hidden")

    // Initialize auto-submit for the filter sidebar
    setTimeout(() => {
      console.log("Looking for filter form in panel")
      const form = this.panelTarget.querySelector('#quotes-filter-form')
      console.log("Found form:", form)
      if (form) {
        console.log("Setting up auto-submit for form")
        this.setupFormAutoSubmit(form)
      }
    }, 100)

    // Focus the close button for accessibility
    const closeButton = this.panelTarget.querySelector('button[data-action*="closeFilter"]')
    if (closeButton) {
      closeButton.focus()
    }
  }

  closeFilter() {
    console.log("Closing filter") // Debug log

    // Don't close if we're in the middle of a form submission
    if (this.isSubmitting) {
      console.log("Preventing close during form submission")
      return
    }

    // Trigger the slide-out animation
    if (this.hasPanelTarget) {
      this.panelTarget.classList.remove("translate-x-0")
      this.panelTarget.classList.add("translate-x-full")
    }

    // Hide backdrop immediately
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.add("hidden")
    }

    // Remove body scroll prevention
    document.body.classList.remove("overflow-hidden")

    // Remove event listeners
    this.removeEventListeners()

    // Hide the panel after animation completes
    setTimeout(() => {
      if (this.hasPanelTarget) {
        this.panelTarget.classList.add("hidden")
      }
    }, 300) // Match the transition duration
  }

  closeOnEscape(event) {
    if (event.key === "Escape") {
      this.closeFilter()
    }
  }

  addEventListeners() {
    document.addEventListener("keydown", this.closeOnEscape)
  }

  removeEventListeners() {
    document.removeEventListener("keydown", this.closeOnEscape)
  }

  setupFormAutoSubmit(form) {
    console.log("Setting up auto-submit for form")

    // Remove any existing listeners to prevent duplicates
    this.removeFormEventListeners(form)

    // Add event listeners to all form inputs for auto-submit
    const inputs = form.querySelectorAll('input, select')
    inputs.forEach(input => {
      if (input.type === 'checkbox' || input.type === 'radio') {
        const listener = () => this.handleFormChange(form)
        input.addEventListener('change', listener)
        this.formEventListeners.set(input, listener)
      } else if (input.type === 'text' || input.type === 'date') {
        const listener = () => this.handleFormChange(form)
        input.addEventListener('input', listener)
        this.formEventListeners.set(input, listener)
      } else if (input.tagName === 'SELECT') {
        const listener = () => this.handleFormChange(form)
        input.addEventListener('change', listener)
        this.formEventListeners.set(input, listener)
      }
    })

    // Also listen for client selection events
    const clientHiddenField = form.querySelector('input[name="settlement_account_id"]')
    if (clientHiddenField) {
      const clientSelectedListener = () => this.handleFormChange(form)
      const clientClearedListener = () => this.handleFormChange(form)
      const changeListener = () => this.handleFormChange(form)
      clientHiddenField.addEventListener('clientSelected', clientSelectedListener)
      clientHiddenField.addEventListener('clientCleared', clientClearedListener)
      clientHiddenField.addEventListener('change', changeListener)
      this.formEventListeners.set(clientHiddenField, {
        clientSelected: clientSelectedListener,
        clientCleared: clientClearedListener,
        change: changeListener
      })
    }
  }

  removeFormEventListeners(form) {
    // Clear existing event listeners
    if (this.formEventListeners) {
      this.formEventListeners.forEach((listener, element) => {
        if (typeof listener === 'function') {
          element.removeEventListener('change', listener)
          element.removeEventListener('input', listener)
        } else if (typeof listener === 'object') {
          // Handle multiple listeners for the same element
          Object.entries(listener).forEach(([event, handler]) => {
            element.removeEventListener(event, handler)
          })
        }
      })
    }
    this.formEventListeners = new Map()
  }

  handleFormChange(form) {
    console.log("Form change detected")

    // Clear existing timeout
    if (this.formSubmitTimeout) {
      clearTimeout(this.formSubmitTimeout)
    }

    // Set new timeout for 500ms debounce
    this.formSubmitTimeout = setTimeout(() => {
      console.log("Submitting form after debounce")
      this.submitForm(form)
    }, 500)
  }

  submitForm(form) {
    console.log("Submitting form")

    // Prevent overlay from closing during form submission
    this.isSubmitting = true

    // Update URL before submitting
    this.updateUrlOnSubmit(form)

    // Ensure the form has the correct Turbo frame attributes
    form.setAttribute('data-turbo-frame', 'quotes_data')
    form.setAttribute('data-turbo-stream', 'true')

    // Submit the form using Turbo's native mechanism
    form.requestSubmit()

    // Reset submission flag after a delay
    setTimeout(() => {
      this.isSubmitting = false
    }, 500)
  }

  updateUrlOnSubmit(form) {
    const formData = new FormData(form)
    const params = new URLSearchParams()

    // Filter out empty values before adding to URL params
    for (const [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`)
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value)
      }
    }

    const queryString = params.toString()
    const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname

    window.history.pushState({}, '', newUrl)
  }

  updateQuotesSearchIcon(value) {
    const clearBtn = document.getElementById('clear-quotes-search-btn')
    const searchIcon = document.getElementById('quotes-search-icon')

    if (clearBtn && searchIcon) {
      if (value.trim() === '') {
        clearBtn.classList.add('hidden')
        searchIcon.classList.remove('hidden')
      } else {
        clearBtn.classList.remove('hidden')
        searchIcon.classList.remove('hidden')
      }
    }
  }

  updateClientSearchIcon(value) {
    const clearBtn = document.getElementById('clear-client-search-btn')
    const searchIcon = document.getElementById('client-search-icon')

    if (clearBtn && searchIcon) {
      if (value.trim() === '') {
        clearBtn.classList.add('hidden')
        searchIcon.classList.remove('hidden')
      } else {
        clearBtn.classList.remove('hidden')
        searchIcon.classList.remove('hidden')
      }
    }
  }

  clearMultiSelectDisplay(component) {
    // Clear the visual display of multi-select components
    const selectedItems = component.querySelectorAll('[data-selected-item]')
    selectedItems.forEach(item => {
      item.remove()
    })

    // Clear any hidden input values
    const hiddenInputs = component.querySelectorAll('input[type="hidden"]')
    hiddenInputs.forEach(input => {
      input.value = ''
    })

    // Reset any display elements
    const displayElements = component.querySelectorAll('[data-multi-select-display]')
    displayElements.forEach(element => {
      element.innerHTML = ''
      element.classList.add('hidden')
    })
  }

  ensureStatusFiltersVisibility() {
    // Get the current status from the URL or form
    const currentStatus = this.getCurrentStatus()

    // Find the status filters section
    const statusFiltersSection = this.panelTarget.querySelector('.status-filters-section')
    if (statusFiltersSection) {
      // Only show status filters if we're on the 'all' tab or no specific status
      if (currentStatus === 'all' || !currentStatus) {
        // Remove any hidden classes to ensure visibility
        statusFiltersSection.classList.remove('hidden')
        statusFiltersSection.classList.remove('opacity-50', 'pointer-events-none')

        // Enable all status filter checkboxes
        const statusCheckboxes = statusFiltersSection.querySelectorAll('input[name="filter_status[]"]')
        statusCheckboxes.forEach(checkbox => {
          checkbox.disabled = false
        })

        const individualCheckboxes = statusFiltersSection.querySelectorAll('input[name="filter_status[]"].status-filter')

        individualCheckboxes.forEach(checkbox => {
          checkbox.checked = false
          checkbox.disabled = false
        })
      } else {
        // For 'drafts' and 'in_progress' tabs, keep status filters hidden
        statusFiltersSection.classList.add('hidden')
      }
    }

    // Also try to find and reinitialize the filter sidebar controller
    const filterSidebarController = this.application.getControllerForElementAndIdentifier(
      this.panelTarget.querySelector('[data-controller*="oms--oms--filter-sidebar"]'),
      'oms--oms--filter-sidebar'
    )

    if (filterSidebarController && typeof filterSidebarController.enableStatusFilters === 'function') {
      filterSidebarController.enableStatusFilters()
    }

    // If the status filters section is still hidden, force it to be visible
    // This handles the case where the component logic hasn't updated yet
    setTimeout(() => {
      if (statusFiltersSection && statusFiltersSection.classList.contains('hidden')) {
        // Only show if we're on the 'all' tab
        if (currentStatus === 'all' || !currentStatus) {
          statusFiltersSection.classList.remove('hidden')
          statusFiltersSection.classList.remove('opacity-50', 'pointer-events-none')

          // Re-enable all checkboxes
          const allCheckboxes = statusFiltersSection.querySelectorAll('input[name="filter_status[]"]')
          allCheckboxes.forEach(checkbox => {
            checkbox.disabled = false
          })
        }
      }
    }, 300)
  }

  getCurrentStatus() {
    // Try to get status from the form first
    const form = this.panelTarget.querySelector('#quotes-filter-form')
    if (form) {
      const statusInput = form.querySelector('input[name="status"]')
      if (statusInput && statusInput.value) {
        return statusInput.value
      }
    }

    // Fallback to URL parameters
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('status')
  }
}
